// import api's and mutations
import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

// import components
import { Box } from '@mui/material';
import { ClaimsAction } from 'components/roster-portal/home/travel-expense-claims';
import {
  Footer,
  Header
} from 'components/roster-portal/home/travel-expense-claims/components';
import TravelDetailDialog from 'components/roster-portal/home/travel-expense-claims/components/TravelDetailDialog';
import WarningDialogAdvanced from 'components/roster-portal/home/travel-expense-claims/components/WarningDialogAdvanced';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import WarningDialog from 'components/shared/WarningDialog/WarningDialog';

// import constants
import { ORANGE } from 'constants/colors';

// import hooks
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { RecoilRoot } from 'recoil';
import { useColumnDefinitions } from './useColumnDefinitions';

// import stores
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useShallow } from 'zustand/shallow';

// import translations
import { t } from 'i18next';

// import types
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import type { PlanUnitMix } from 'components/types';
import type { TravelMovementPerDayResponse } from 'generated/index';
import type { MouseEvent } from 'react';
import type {
  ExtendedTravelMovementPerDayResponse,
  TravelMovementsProps
} from './TravelMovements.types';

// import utils
import { calculateTotalKmByType } from 'components/roster-portal/home/travel-expense-claims/utils';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getCurrentlyLoggedInEmployeeId } from 'utils/misc';
import { v4 as uuidv4 } from 'uuid';

dayjs.extend(utc);

const TravelMovements = ({
  page = 'START',
  selectedEmployeeId,
  travelExchangeClaimsMonth = null,
  showActions = false,
  isTravelMovementsBasedOnPlanUnit = false,
  showEmployeeName = false
}: TravelMovementsProps) => {
  'use memo';

  const columns = useColumnDefinitions({ showEmployeeName });
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnitMix[]>([]);
  const {
    selectedDate: storeDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange,
    openPreviousMonthsRange,
    setOpenPreviousMonthsRange
  } = useDateNavigationHoursStore((state) => state);
  const { period, setPeriod } = useDateNavigationStore(
    useShallow((state) => state)
  );

  // The custom period (opened from the quality-monitor tile) only applies to
  // the plan-unit-based overview — the per-employee screen always stays on month.
  const isCustomDateRange =
    isTravelMovementsBasedOnPlanUnit && period === 'custom';

  // useMemo is required here despite 'use memo' because the React compiler cannot
  // guarantee referential stability for a value derived from two nullable external
  // sources with a fallback (new Date()). Without it, a new Date instance would be
  // created on every render, causing downstream hooks that depend on selectedDate
  // to re-run unnecessarily.
  const selectedDate = useMemo(
    () => travelExchangeClaimsMonth ?? storeDate ?? new Date(),
    [travelExchangeClaimsMonth, storeDate]
  );
  const loggedInUserEmployeeId = +getCurrentlyLoggedInEmployeeId();
  const [selectedRows, setSelectedRows] = useState<
    ExtendedTravelMovementPerDayResponse[]
  >([]);

  const startDate = isCustomDateRange
    ? dayjs(selectedDateRange?.[0]).format('YYYY-MM-DD')
    : dayjs(selectedDate).startOf('month').format('YYYY-MM-DD');
  const endDate = isCustomDateRange
    ? dayjs(selectedDateRange?.[1]).format('YYYY-MM-DD')
    : dayjs(selectedDate).endOf('month').format('YYYY-MM-DD');

  const organizationalUnitIds =
    selectedPlanUnits &&
    Array.from(
      // create an unique set of costCenter ids
      new Set(
        selectedPlanUnits
          .map((unit) =>
            'costCenters' in unit &&
            Array.isArray(unit.costCenters) &&
            unit.costCenters?.length > 0
              ? unit.costCenters.map(({ id }) => id)
              : unit.id
          )
          .flat()
      )
    ).join(',');

  const [showDetailDialog, setShowDetailDialog] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<
    [boolean, ClaimsAction | null]
  >([false, null]);
  const [showRejectDialog, setShowRejectDialog] = useState<
    [boolean, ClaimsAction | null]
  >([false, null]);
  const [activeClaim, setActiveClaim] =
    useState<TravelMovementPerDayResponse | null>(null);

  const [onlyApprovableTravelMovements, setOnlyApprovableTravelMovements] =
    useState<boolean>(true);
  // Custom periods only ever show approvable travel movements, regardless of
  // the toggle's own state, which is forced on and disabled in that case.
  const effectiveOnlyApprovableTravelMovements = isCustomDateRange
    ? true
    : onlyApprovableTravelMovements;

  const isOverview = !page || page === 'START';
  const isEmployeeCard = page === 'EMPLOYEE_CARD';
  const isEmployeePortal = page === 'EMPLOYEE_PORTAL';
  const isEmployee = isEmployeeCard || isEmployeePortal;
  const [shouldFetch, setShouldFetch] = useState(false);

  const queryClient = useQueryClient();

  // Expense claims by employee
  const {
    data: dataByEmployee,
    refetch: refetchByEmployee,
    isLoading: isLoadingByEmployee
  } = queries.useFindTravelMovementsByEmployee(
    startDate,
    endDate,
    selectedEmployeeId as number,
    {
      enabled:
        !isTravelMovementsBasedOnPlanUnit &&
        !!(loggedInUserEmployeeId || selectedEmployeeId)
    }
  );

  // Expense claims by organizational unit
  const {
    data: dataByUnit,
    refetch: refetchByUnit,
    isLoading: isLoadingByUnit
  } = queries.useFindTravelMovementsByOrganizationalUnits(
    startDate,
    endDate,
    effectiveOnlyApprovableTravelMovements,

    // @ts-ignore
    organizationalUnitIds as string[],
    {
      enabled:
        isTravelMovementsBasedOnPlanUnit &&
        shouldFetch &&
        !!organizationalUnitIds &&
        dayjs(startDate).isValid() &&
        dayjs(endDate).isValid()
    }
  );

  const data = isTravelMovementsBasedOnPlanUnit ? dataByUnit : dataByEmployee;
  const refetchTravelMovements = isTravelMovementsBasedOnPlanUnit
    ? refetchByUnit
    : refetchByEmployee;
  const isLoading = isTravelMovementsBasedOnPlanUnit
    ? isLoadingByUnit
    : isLoadingByEmployee;

  // useMemo is required here even though 'use memo' is active: uuidv4() is
  // non-deterministic, so the React Compiler cannot safely memoize this
  // mapping. Without it, new UUIDs would be generated on every render,
  // breaking row identity (selection state, row actions) in TableCRUDLayout.
  const rows = useMemo(
    () =>
      data?.map((response: TravelMovementPerDayResponse) => ({
        ...response,
        uniqueId: uuidv4(),
        disableSelection: !response.canBeApproved
      })) ?? [],
    [data]
  );

  const handleCloseDetailDialog = () => {
    setShowDetailDialog(false);
    setActiveClaim(null);
  };

  const totalKmPrivate = calculateTotalKmByType(rows, 'PRIVATE');
  const totalKmBusiness = calculateTotalKmByType(rows, 'BUSINESS');

  const { mutate: approveMutation, isLoading: isLoadingApproveMutation } =
    mutations.useApproveExpenseClaims({
      onSuccess: () => {
        const queryKey = isEmployee
          ? queryKeys.findTravelMovementsByEmployee(
              startDate,
              endDate,
              selectedEmployeeId as number
            )
          : queryKeys.findTravelMovementsByOrganizationalUnits(
              startDate,
              endDate,
              effectiveOnlyApprovableTravelMovements,
              organizationalUnitIds.split(',').map(Number)
            );
        queryClient.invalidateQueries(queryKey);
        refetchTravelMovements();
        showSuccessSnackbar();
        setSelectedRows([]);
        setShowWarningDialog([false, null]);
      },
      onError: (e: any) => {
        showErrorSnackbar(e.message);
      }
    });

  const { mutate: rejectMutation, isLoading: isLoadingRejectMutation } =
    mutations.useRejectExpenseClaims({
      onSuccess: () => {
        const queryKey = isEmployee
          ? queryKeys.findTravelMovementsByEmployee(
              startDate,
              endDate,
              selectedEmployeeId as number
            )
          : queryKeys.findTravelMovementsByOrganizationalUnits(
              startDate,
              endDate,
              effectiveOnlyApprovableTravelMovements,
              organizationalUnitIds.split(',').map(Number)
            );
        queryClient.invalidateQueries(queryKey);
        refetchTravelMovements();
        showSuccessSnackbar();
        setSelectedRows([]);
        setShowRejectDialog([false, null]);
      },
      onError: (e: any) => {
        showErrorSnackbar(e.message);
      }
    });

  const approveClaims = () => {
    const expenseClaimIds = selectedRows
      .filter((row) => row.canBeApproved && row.id !== undefined)
      .map(({ id }) => id as string);

    approveMutation({ expenseClaimIds });
  };

  const rejectClaims = (formData: { remark: string }) => {
    const expenseClaimIds = selectedRows
      .map(({ id }) => id)
      .filter((id): id is string => id !== undefined);

    rejectMutation({ expenseClaimIds, statusNote: formData.remark });
  };

  const handleAction = (action: ClaimsAction) => {
    switch (action) {
      case ClaimsAction.APPROVE:
        setShowWarningDialog([true, ClaimsAction.APPROVE]);
        break;
      case ClaimsAction.REJECT:
        setShowRejectDialog([true, ClaimsAction.REJECT]);
        break;
      default:
        break;
    }
  };

  const handleActionClick = (
    uniqueId: string | number,
    row: ExtendedTravelMovementPerDayResponse,
    action: ClaimsAction,
    event?: MouseEvent<HTMLButtonElement>
  ) => {
    event?.preventDefault();
    event?.stopPropagation();

    row &&
      !selectedRows.some((r) => r.uniqueId === row.uniqueId) &&
      setSelectedRows((prev) => [...prev, row]);

    setActiveClaim(rows.find((r) => r.uniqueId === uniqueId)!);
    handleAction?.(action);
  };

  const handleToggleChange = () => {
    setOnlyApprovableTravelMovements((prev) => !prev);
  };

  useEffect(() => {
    if (selectedPlanUnits.length > 0) setShouldFetch(true);
  }, [selectedPlanUnits, selectedDate]);

  const [
    hasAppliedOpenPreviousMonthsRange,
    setHasAppliedOpenPreviousMonthsRange
  ] = useState(false);

  // Normalize once, on mount: an explicit intent from the tile takes over the
  // range and switches to 'custom'; otherwise 'month' is forced explicitly, so
  // a custom period inherited from another screen (via the shared period
  // store) never leaks in. There is no race with DateNavigation's own mount
  // effect: this screen passes `persistPeriodPreference={false}` (Header), so
  // that effect only mirrors `defaultPeriod` back and never restores the saved
  // preference. The applied-flag (rather than reacting to the intent going back
  // to null once consumed) keeps this a true one-shot: consuming the intent
  // must not immediately flip the period back to 'month'.
  useEffect(() => {
    if (!isTravelMovementsBasedOnPlanUnit || hasAppliedOpenPreviousMonthsRange)
      return;

    if (openPreviousMonthsRange) {
      setSelectedDateRange(openPreviousMonthsRange);
      setPeriod('custom');
      setOpenPreviousMonthsRange(null);
    } else {
      setPeriod('month');
    }
    setHasAppliedOpenPreviousMonthsRange(true);
  }, [
    isTravelMovementsBasedOnPlanUnit,
    hasAppliedOpenPreviousMonthsRange,
    openPreviousMonthsRange,
    setSelectedDateRange,
    setPeriod,
    setOpenPreviousMonthsRange
  ]);

  return (
    <RecoilRoot>
      <Header
        onAction={handleAction}
        onSelectPlanUnit={setSelectedPlanUnits}
        onSelectedDate={setSelectedDate}
        onSelectedDateRange={setSelectedDateRange}
        selectedDate={dayjs(selectedDate).toDate()}
        selectedDateRange={selectedDateRange}
        period={isCustomDateRange ? 'custom' : 'month'}
        dateNavigationOptions={
          isTravelMovementsBasedOnPlanUnit ? ['month', 'custom'] : undefined
        }
        allowDateNavigationMenuOptions={isTravelMovementsBasedOnPlanUnit}
        selection={selectedRows}
        showActions={isOverview}
        showPlanUnitSelector={isOverview}
        isTravelMovements
        onlyApprovableTravelMovements={effectiveOnlyApprovableTravelMovements}
        handleToggleChange={handleToggleChange}
        approveToggleDisabled={isCustomDateRange}
      />
      <Box>
        <TableCRUDLayout<ExtendedTravelMovementPerDayResponse>
          rows={rows}
          getRowId={(row) => row?.uniqueId}
          columns={columns}
          loading={
            isLoading || isLoadingRejectMutation || isLoadingApproveMutation
          }
          pageSize={50}
          dynamicHeight={true}
          editable={false}
          hideFooter={rows?.length === 0}
          actionsThreshold={4}
          pageSizeOptions={[5]}
          checkboxSelection={rows.some(
            ({ disableSelection }) => !disableSelection
          )}
          customSelectionModel={{
            type: 'include',
            ids: new Set<GridRowId>(
              selectedRows.map(({ uniqueId }) => uniqueId)
            )
          }}
          onSelectionChange={(selectionModel: GridRowSelectionModel) => {
            const selectedRowsData = rows.filter((row) =>
              selectionModel.ids.has(row.uniqueId)
            );
            setSelectedRows(selectedRowsData);
          }}
          footer={
            <Footer>
              {`${t('travelMovements.totalKm')}: ${totalKmPrivate} (${t(
                'travelMovements.type.PRIVATE'
              )})
                - ${totalKmBusiness} (${t('travelMovements.type.BUSINESS')})`}
            </Footer>
          }
          onViewClick={(
            id: string | number,
            _row,
            event?: MouseEvent<HTMLButtonElement>
          ) => {
            event?.preventDefault();
            event?.stopPropagation();
            setActiveClaim(rows.find((r) => r.uniqueId === id)!);
            setShowDetailDialog(true);
          }}
          customActions={(id, row) => [
            {
              tooltipTitle: t('travelExpenseClaims.actions.APPROVE'),
              icon: 'check',
              onClick: (id, _row, _handlers, event) =>
                row && handleActionClick(id, row, ClaimsAction.APPROVE, event),
              hidden:
                !showActions ||
                !row?.canBeApproved ||
                row?.status !== 'SUBMITTED'
            },
            {
              tooltipTitle: t('travelExpenseClaims.actions.REJECT'),
              icon: 'xmark',
              onClick: (id, _row, _handlers, event) =>
                row && handleActionClick(id, row, ClaimsAction.REJECT, event),
              hidden:
                !showActions ||
                !row?.canBeApproved ||
                row?.status !== 'SUBMITTED'
            },
            {
              tooltipTitle: t('travelMovements.misMatch'),
              icon: 'triangle-exclamation',
              hidden: !row?.isMismatch || row?.status === 'CREDITED',
              disableRipple: true,
              disableTouchRipple: true,
              sx: {
                color: ORANGE,
                '&:hover': {
                  backgroundColor: 'transparent',
                  cursor: 'default'
                }
              }
            }
          ]}
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'asc' }]
            }
          }}
          sx={{
            mt: 1,
            // Hide the checkbox if the row/input is disabled
            '.MuiCheckbox-root input[type="checkbox"]:disabled + *': {
              display: 'none'
            }
          }}
        />
      </Box>

      <WarningDialog
        title={t('travelExpenseClaims.warning', {
          action: t(`travelExpenseClaims.actions.${showWarningDialog[1]}`)
        })}
        open={showWarningDialog[0]}
        onSubmit={() => approveClaims()}
        onCancel={() => {
          setShowWarningDialog([false, null]);
        }}
      />

      <WarningDialogAdvanced
        title={t('travelExpenseClaims.warning', {
          action: t(`travelExpenseClaims.actions.${showRejectDialog[1]}`)
        })}
        open={showRejectDialog[0]}
        selectedRows={selectedRows}
        onSubmit={(formData) => rejectClaims(formData)}
        onCancel={() => {
          setShowRejectDialog([false, null]);
        }}
      />

      <TravelDetailDialog
        claim={activeClaim}
        isOpen={showDetailDialog}
        onClose={handleCloseDetailDialog}
        isTravelMovements={true}
        refetchTravelMovements={refetchTravelMovements}
      />

      <LoadingOverlay loading={isLoading} />
    </RecoilRoot>
  );
};

export default TravelMovements;
